/**
 * System notifications for alerts.
 *
 * Shown through the service worker registration where there is one, because
 * that is the only route that works on Android and in an installed PWA —
 * `new Notification()` throws there. The constructor is the fallback for a
 * desktop browser without a worker (the Vite dev server, for one).
 */

export type NotifyPermission = NotificationPermission | 'unsupported';

export function notifyPermission(): NotifyPermission {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/** Must be called from a click: browsers ignore permission prompts without one. */
export async function requestNotifyPermission(): Promise<NotifyPermission> {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/**
 * Never throws: like the sound, a notification is on top of an alert card that
 * is already on screen.
 *
 * @param tag Replaces an earlier notification with the same tag instead of
 *   stacking a second one for the same alert.
 */
export async function showNotification(title: string, body: string, tag: string): Promise<void> {
  if (notifyPermission() !== 'granted') return;
  const options: NotificationOptions = {
    body,
    tag,
    icon: '/pwa-192-v5.png',
    // Android draws the badge as a flat silhouette from its alpha channel, so
    // it has to be a white shape on transparent — the full-colour icon here
    // showed as a solid white square.
    badge: '/badge-96-v5.png',
    // A price alert is something you asked to be interrupted by; let it stay
    // until it is dealt with rather than sliding away after a few seconds.
    requireInteraction: true,
  };
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.showNotification(title, options);
      return;
    }
    new Notification(title, options);
  } catch {
    /* blocked or unsupported — the in-app card still shows */
  }
}

/**
 * Take down every notification whose tag starts with `prefix`, except `keep`.
 *
 * Sweeps by what is actually on screen rather than by what this page showed,
 * so it also clears notifications left by an earlier page load or another tab.
 */
export async function closeNotificationsExcept(prefix: string, keep: ReadonlySet<string>): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const open = (await reg?.getNotifications()) ?? [];
    for (const n of open) if (n.tag.startsWith(prefix) && !keep.has(n.tag)) n.close();
  } catch {
    /* nothing to close */
  }
}

/** Take down a notification shown with `tag`, e.g. when its alert is deleted. */
export async function closeNotification(tag: string): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const open = (await reg?.getNotifications({ tag })) ?? [];
    for (const n of open) n.close();
  } catch {
    /* nothing to close */
  }
}
